import { Download } from 'lucide-react'
import { DownloadsSpaceFields } from '@/components/features/downloads/DownloadsSpaceFields'
import {
  defaultDownloadsConfig,
  toSpaceInput,
  validateDownloadsConfig
} from '@/components/features/downloads/downloadUtils'
import { apiFieldErrors } from '@/components/features/classifieds/classifiedUtils'
import { sdk } from '@/lib/sdk'
import { isEmptyList } from '../emptyList'
import type { ModuleDefinition } from '../types'
import { downloadRow } from '../layouts/rows'
import { lazyPage } from '@/lib/lazyPage'

const DownloadsPage = lazyPage(() => import('@/pages/Downloads'), 'DownloadsPage')
const DownloadDetailsPage = lazyPage(() => import('@/pages/DownloadDetails'), 'DownloadDetailsPage')

export const downloadsModule: ModuleDefinition = {
  key: 'downloads',
  icon: Download,
  iconName: 'download',
  namespaces: ['downloads'],
  ready: true,
  /* F-198: the default is what the page always drew. */
  layouts: { fallback: 'cards', renders: ['cards', 'list', 'compact'], toRow: downloadRow },
  ListPage: DownloadsPage,
  renderDetail: ({ spaceData }) => <DownloadDetailsPage spaceId={spaceData.id} spaceData={spaceData} />,
  /* Mode (repository or one file), categories and approval (F-75). */
  setup: {
    wizardStep: 'own',
    stepLabelKey: 'common:spaces.stepDownloads',
    stepHintKey: 'downloads:config.stepHint',
    /* Only the team posts by default: files are the community's, members are let in by the owner. */
    defaultWhoCanPost: 'staff',
    defaultConfig: (t) => defaultDownloadsConfig(t),
    validate: (config, t) => validateDownloadsConfig(config, t),
    Fields: DownloadsSpaceFields,
    persist: {
      type: 'endpoint',
      /* Mode, categories and approval live in `DownloadSpace`. */
      save: (spaceId, config) => sdk.downloads.updateSpace(spaceId, toSpaceInput(config)),
      load: async (spaceId) => {
        const config = await sdk.downloads.getSpace(spaceId)
        if (!config.canConfigure) return null
        return {
          mode: config.mode,
          requireApproval: config.requireApproval,
          categories: config.categories.map((c) => ({ id: c.id, name: c.name, itemsCount: c.itemsCount }))
        }
      },
      /* A category with items, or "one file" with several. */
      errorsFromSaveFailure: (err) => {
        const fields = apiFieldErrors(err)
        return fields.categories ? { categories: fields.categories } : null
      }
    }
  },
  singleItem: { isEmpty: async (spaceId) => isEmptyList(await sdk.downloads.listItems({ spaceId, limit: 1 })) },
  workflow: { trigger: { block: 'downloads', families: ['download'] } }
}
